import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "tableBody",
    "nameInput",
    "unitDisplay",
    "priceDisplay",
    "quantityInput",
    "costDisplay",
    "addButton",
    "suggestions",
    "totalCost",
    "rowTemplate",
    "suggestionTemplate",
    "ingredientRow",
    "errorContainer",
    "unitSelect",
    "priceInput",
    "createModeIndicator",
  ];

  static values = {
    establishmentId: Number,
    searchUrl: String,
  };

  static outlets = ["cost-calculator"];

  connect() {
    this.rowIndex = this.tableBodyTarget.rows.length;
    this.currentIngredient = null;
    this.searchTimeout = null;
  }

  searchIngredients(event) {
    const query = this.nameInputTarget.value.trim();

    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      if (query.length < 2) {
        this.hideSuggestions();
        return;
      }
      this.performSearch(query);
    }, 250);
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${query}`, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      const data = await response.json();
      const ingredients = Array.isArray(data) ? data : data.ingredients || [];
      this.showSuggestions(ingredients, query);
    } catch (error) {
      console.error("Search error:", error);
    }
  }

  showSuggestions(ingredients, query) {
    this.suggestionsTarget.innerHTML = "";
    this.suggestionsTarget.classList.remove("hidden");

    if (ingredients.length > 0) {
      ingredients.forEach((ingredient) => {
        const suggestionEl = this.createSuggestionElement(ingredient);
        this.suggestionsTarget.appendChild(suggestionEl);
      });
    }

    this.showNewIngredientOption(query, ingredients.length === 0);
  }

  createSuggestionElement(ingredient) {
    const template = this.suggestionTemplateTarget.content.cloneNode(true);
    const element = template.querySelector(".suggestion-item");

    element.querySelector(".suggestion-name").textContent = ingredient.name;
    element.querySelector(
      ".suggestion-unit"
    ).textContent = `(${ingredient.unit})`;
    element.querySelector(".suggestion-price").textContent =
      this.formatCurrency(ingredient.price);
    element.dataset.ingredient = JSON.stringify(ingredient);

    return element;
  }

  showNewIngredientOption(query, isOnlyOption) {
    const newOptionEl = document.createElement("div");
    newOptionEl.className =
      "suggestion-item px-3 py-2 hover:bg-base-200 cursor-pointer border-t border-base-300/50";
    newOptionEl.innerHTML = `
      <div class="flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <span class="font-medium">Créer:</span>
        <span class="ml-1 italic">"${query}"</span>
      </div>
    `;
    newOptionEl.dataset.action = "click->ingredients-table#createNewIngredient";
    newOptionEl.dataset.name = query;
    this.suggestionsTarget.appendChild(newOptionEl);
  }

  selectSuggestion(event) {
    const ingredient = JSON.parse(event.currentTarget.dataset.ingredient);
    this.currentIngredient = ingredient;

    this.nameInputTarget.value = ingredient.name;
    this.unitDisplayTarget.textContent = ingredient.unit;
    this.priceDisplayTarget.textContent = this.formatCurrency(ingredient.price);

    this.unitDisplayTarget.classList.remove("hidden");
    this.priceDisplayTarget.classList.remove("hidden");
    this.unitSelectTarget.classList.add("hidden");
    this.priceInputTarget.classList.add("hidden");
    this.createModeIndicatorTarget.classList.add("hidden");

    this.quantityInputTarget.disabled = false;
    this.quantityInputTarget.focus();
    this.hideSuggestions();
  }

  createNewIngredient(event) {
    const name = event.currentTarget.dataset.name;
    this.currentIngredient = { name: name, isNew: true };

    this.nameInputTarget.value = name;

    this.unitDisplayTarget.classList.add("hidden");
    this.priceDisplayTarget.classList.add("hidden");

    this.unitSelectTarget.classList.remove("hidden");
    this.priceInputTarget.classList.remove("hidden");
    this.createModeIndicatorTarget.classList.remove("hidden");

    this.quantityInputTarget.disabled = false;
    this.unitSelectTarget.focus();
    this.hideSuggestions();
  }

  hideSuggestions() {
    this.suggestionsTarget.classList.add("hidden");
  }

  calculateCost() {
    if (!this.currentIngredient) return;

    const quantity = parseFloat(this.quantityInputTarget.value) || 0;
    let price;

    if (this.currentIngredient.isNew) {
      price = parseFloat(this.priceInputTarget.value) || 0;
    } else {
      price = this.currentIngredient.price;
    }

    const cost = quantity * price;
    this.costDisplayTarget.textContent = this.formatCurrency(cost);

    if (quantity > 0 && (this.currentIngredient.price || price > 0)) {
      this.addButtonTarget.classList.remove("opacity-0");
    } else {
      this.addButtonTarget.classList.add("opacity-0");
    }
  }

  addIngredient() {
    const quantity = parseFloat(this.quantityInputTarget.value);
    if (!this.currentIngredient || !quantity || quantity <= 0) return;

    let ingredientData;
    if (this.currentIngredient.isNew) {
      const unit = this.unitSelectTarget.value;
      const price = parseFloat(this.priceInputTarget.value);
      if (!unit || !price || price <= 0) {
        // Add validation feedback
        return;
      }
      ingredientData = { ...this.currentIngredient, unit: unit, price: price };
    } else {
      ingredientData = this.currentIngredient;
    }

    this.createIngredientRow(ingredientData, quantity);
    this.resetNewRowState();
    this.updateTotalCost();
  }

  createIngredientRow(ingredient, quantity) {
    const template = this.rowTemplateTarget.content.cloneNode(true);
    const row = template.querySelector("tr");
    const index = this.rowIndex++;

    row.querySelector(
      '[data-ingredients-table-target="ingredientName"]'
    ).textContent = ingredient.name;
    row.querySelector(
      '[data-ingredients-table-target="ingredientUnit"]'
    ).textContent = ingredient.unit;
    row.querySelector(
      '[data-ingredients-table-target="ingredientPrice"]'
    ).textContent = this.formatCurrency(ingredient.price);

    const quantityField = row.querySelector(
      '[data-ingredients-table-target="quantityField"]'
    );
    quantityField.value = quantity;
    quantityField.name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][quantity]`;

    const cost = quantity * ingredient.price;
    row.querySelector('[data-ingredients-table-target="rowCost"]').textContent =
      this.formatCurrency(cost);

    if (ingredient.isNew) {
      row
        .querySelector('[data-ingredients-table-target="newBadge"]')
        .classList.remove("hidden");
      row.querySelector(
        '[data-ingredients-table-target="newIngredientNameInput"]'
      ).name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][ingredient_attributes][name]`;
      row.querySelector(
        '[data-ingredients-table-target="newIngredientNameInput"]'
      ).value = ingredient.name;
      row.querySelector(
        '[data-ingredients-table-target="newIngredientUnitInput"]'
      ).name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][ingredient_attributes][unit]`;
      row.querySelector(
        '[data-ingredients-table-target="newIngredientUnitInput"]'
      ).value = ingredient.unit;
      row.querySelector(
        '[data-ingredients-table-target="newIngredientPriceInput"]'
      ).name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][ingredient_attributes][price]`;
      row.querySelector(
        '[data-ingredients-table-target="newIngredientPriceInput"]'
      ).value = ingredient.price;
    } else {
      row.querySelector(
        '[data-ingredients-table-target="masterIngredientIdInput"]'
      ).name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][master_ingredient_id]`;
      row.querySelector(
        '[data-ingredients-table-target="masterIngredientIdInput"]'
      ).value = ingredient.master_ingredient_id || "";
      row.querySelector(
        '[data-ingredients-table-target="ingredientIdInput"]'
      ).name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][ingredient_id]`;
      row.querySelector(
        '[data-ingredients-table-target="ingredientIdInput"]'
      ).value = ingredient.ingredient_id || ingredient.id;
      row.querySelector(
        '[data-ingredients-table-target="supplierIdInput"]'
      ).name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][supplier_id]`;
      row.querySelector(
        '[data-ingredients-table-target="supplierIdInput"]'
      ).value = ingredient.supplier_id || "";
    }

    const tsiIdField = row.querySelector(
      '[data-ingredients-table-target="tsiIdInput"]'
    );
    if (ingredient.technical_sheet_ingredient_id) {
      tsiIdField.name = `technical_sheet[technical_sheet_ingredients_attributes][${index}][id]`;
      tsiIdField.value = ingredient.technical_sheet_ingredient_id;
    }

    this.tableBodyTarget.appendChild(row);
  }

  removeIngredient(event) {
    const row = event.currentTarget.closest("tr");
    const destroyInput = row.querySelector(
      '[data-ingredients-table-target="destroyInput"]'
    );

    if (destroyInput) {
      destroyInput.value = "1";
      row.classList.add("hidden");
    } else {
      row.remove();
    }

    if (this.hasCostCalculatorOutlet) {
      this.costCalculatorOutlet.onIngredientsUpdate();
    }
  }

  updateRowCost(event) {
    const row = event.currentTarget.closest("tr");
    const quantity = parseFloat(event.currentTarget.value) || 0;
    const priceText = row.querySelector(
      '[data-ingredients-table-target="ingredientPrice"]'
    ).textContent;
    const price =
      parseFloat(priceText.replace(/[€\s]/g, "").replace(",", ".")) || 0;

    const cost = quantity * price;
    row.querySelector('[data-ingredients-table-target="rowCost"]').textContent =
      this.formatCurrency(cost);

    if (this.hasCostCalculatorOutlet) {
      this.costCalculatorOutlet.onIngredientsUpdate();
    }
  }

  updateTotalCost() {
    let total = 0;
    this.tableBodyTarget.querySelectorAll("tr:not(.hidden)").forEach((row) => {
      const costText = row.querySelector(
        '[data-ingredients-table-target="rowCost"]'
      ).textContent;
      total +=
        parseFloat(costText.replace(/[€\s]/g, "").replace(",", ".")) || 0;
    });

    this.totalCostTarget.textContent = this.formatCurrency(total);
    if (this.hasCostCalculatorOutlet) {
      this.costCalculatorOutlet.onIngredientsUpdate();
    }
  }

  resetNewRowState() {
    this.nameInputTarget.value = "";
    this.quantityInputTarget.value = "";
    this.costDisplayTarget.textContent = this.formatCurrency(0);
    this.unitDisplayTarget.textContent = "Unité";
    this.priceDisplayTarget.textContent = "Prix/u";
    this.quantityInputTarget.disabled = true;
    this.addButtonTarget.classList.add("opacity-0");
    this.currentIngredient = null;
    this.nameInputTarget.focus();
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      const selectedSuggestion = this.suggestionsTarget.querySelector(
        ".suggestion-item.selected"
      );
      if (selectedSuggestion) {
        selectedSuggestion.click();
      } else if (
        this.addButtonTarget.classList.contains("opacity-0") === false
      ) {
        this.addIngredient();
      }
    }
  }

  preventFormSubmission(event) {
    if (event.key === "Enter") {
      const targetElement = event.target;
      if (targetElement.matches("input, select")) {
        event.preventDefault();
      }
    }
  }
}
